import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, ChevronLeft, ChevronRight, Link2, ArrowLeft } from 'lucide-react';
import SynchronizedPosePlayer from '../../SynchronizedPosePlayer';
import CanvasPoseOverlayPlayer from '../../CanvasPoseOverlayPlayer';
import FrameAwareCoaching from '../../FrameAwareCoaching';
import { API_URL } from '../../config/api';
import authService from '../../services/authService';
import './VideoComparisonViewer.css';

const SPEED_ORDER = [500, 250, 125];
const SPEED_LABELS = { 500: '¼×', 250: '½×', 125: '1×' };

const clamp = (val, min, max) => Math.max(min, Math.min(val, max));

const VideoComparisonViewer = ({ videoA, videoB, onClose, fpsA = 30, fpsB = 30 }) => {
  const playerARef = useRef(null);
  const playerBRef = useRef(null);
  const playLoopRef = useRef(false);
  const syncFrameRef = useRef(0);

  // Sync state
  const [syncFrame, setSyncFrame] = useState(0);
  const [frameA, setFrameA] = useState(0);
  const [frameB, setFrameB] = useState(0);
  const [offsetA, setOffsetA] = useState(0);
  const [offsetB, setOffsetB] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(250);

  // Phase data
  const [phasesA, setPhasesA] = useState(null);
  const [phasesB, setPhasesB] = useState(null);

  // Coaching data (pre-loaded from bulk frames)
  const [coachingA, setCoachingA] = useState(null);
  const [coachingB, setCoachingB] = useState(null);
  const [coachingMapA, setCoachingMapA] = useState(null);
  const [coachingMapB, setCoachingMapB] = useState(null);

  const totalA = videoA.totalFrames || 0;
  const totalB = videoB.totalFrames || 0;
  const maxSyncFrame = Math.max(totalA - offsetA, totalB - offsetB) - 1;
  const isSynced = offsetA !== 0 || offsetB !== 0;

  // Fetch phase boundaries and coaching data from bulk frame data
  useEffect(() => {
    const fetchPhases = async (vid, setPhases, setCoachingMap) => {
      try {
        const res = await fetch(`${API_URL}/api/v1/videos/${vid}/frames`, {
          headers: { ...authService.getAuthHeader() },
        });
        if (!res.ok) return;
        const data = await res.json();
        const frames = data.frames || [];
        if (frames.length === 0) return;

        const phases = {};
        const cMap = {};
        let currentPhase = null;
        for (const f of frames) {
          const p = f.element_phase || f.phase || f.biomechanics_data?.phase;
          if (p && p !== currentPhase) {
            phases[p] = f.frame_number;
            currentPhase = p;
          }
          if (f.coaching) {
            cMap[f.frame_number] = f.coaching;
          }
        }
        setPhases(phases);
        setCoachingMap(cMap);
      } catch (e) {
        console.warn('Failed to fetch phases:', e);
      }
    };

    fetchPhases(videoA.videoId, setPhasesA, setCoachingMapA);
    fetchPhases(videoB.videoId, setPhasesB, setCoachingMapB);
  }, [videoA.videoId, videoB.videoId]);

  // Update coaching when frames change (dict lookup, no API call)
  useEffect(() => {
    setCoachingA(coachingMapA?.[frameA] || null);
  }, [frameA, coachingMapA]);

  useEffect(() => {
    setCoachingB(coachingMapB?.[frameB] || null);
  }, [frameB, coachingMapB]);

  // Seek both players to the given sync position
  const seekBoth = useCallback((newSync) => {
    const newA = clamp(offsetA + newSync, 0, Math.max(totalA - 1, 0));
    const newB = clamp(offsetB + newSync, 0, Math.max(totalB - 1, 0));
    setSyncFrame(newSync);
    setFrameA(newA);
    setFrameB(newB);
    playerARef.current?.seekToFrame(newA);
    playerBRef.current?.seekToFrame(newB);
  }, [offsetA, offsetB, totalA, totalB]);

  // Time-driven playback using requestAnimationFrame
  // Calculates target frame from elapsed time — no async seeks, no timeouts
  const startPlayback = useCallback(() => {
    playLoopRef.current = true;
    setIsPlaying(true);
    const startTime = performance.now();
    const startFrame = syncFrame;
    syncFrameRef.current = startFrame;

    const tick = () => {
      if (!playLoopRef.current) return;

      const elapsed = performance.now() - startTime;
      const targetFrame = startFrame + Math.floor(elapsed / playbackSpeed);

      if (targetFrame >= maxSyncFrame) {
        // Seek to final frame, then stop
        const finalA = clamp(offsetA + maxSyncFrame, 0, Math.max(totalA - 1, 0));
        const finalB = clamp(offsetB + maxSyncFrame, 0, Math.max(totalB - 1, 0));
        playerARef.current?.seekToFrame(finalA);
        playerBRef.current?.seekToFrame(finalB);
        setSyncFrame(maxSyncFrame);
        setFrameA(finalA);
        setFrameB(finalB);
        syncFrameRef.current = maxSyncFrame;
        playLoopRef.current = false;
        setIsPlaying(false);
        return;
      }

      if (targetFrame !== syncFrameRef.current) {
        const newA = clamp(offsetA + targetFrame, 0, Math.max(totalA - 1, 0));
        const newB = clamp(offsetB + targetFrame, 0, Math.max(totalB - 1, 0));
        playerARef.current?.seekToFrame(newA);
        playerBRef.current?.seekToFrame(newB);
        syncFrameRef.current = targetFrame;
        setSyncFrame(targetFrame);
        setFrameA(newA);
        setFrameB(newB);
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [syncFrame, maxSyncFrame, offsetA, offsetB, totalA, totalB, playbackSpeed]);

  const stopPlayback = useCallback(() => {
    playLoopRef.current = false;
    setIsPlaying(false);
  }, []);

  // Stop playback loop on unmount
  useEffect(() => {
    return () => { playLoopRef.current = false; };
  }, []);

  // Solo step - move only one video
  const soloStep = useCallback((video, dir) => {
    if (video === 'A') {
      const newFrame = clamp(frameA + dir, 0, totalA - 1);
      setFrameA(newFrame);
      playerARef.current?.seekToFrame(newFrame);
    } else {
      const newFrame = clamp(frameB + dir, 0, totalB - 1);
      setFrameB(newFrame);
      playerBRef.current?.seekToFrame(newFrame);
    }
  }, [frameA, frameB, totalA, totalB]);

  // Sync step - move both videos together
  const syncStep = useCallback((dir) => {
    seekBoth(clamp(syncFrame + dir, 0, maxSyncFrame));
  }, [syncFrame, maxSyncFrame, seekBoth]);

  // Sync play/pause toggle
  const syncTogglePlay = useCallback(() => {
    if (isPlaying) {
      stopPlayback();
    } else {
      startPlayback();
    }
  }, [isPlaying, startPlayback, stopPlayback]);

  // Align from current positions
  const alignFromCurrent = useCallback(() => {
    setOffsetA(frameA);
    setOffsetB(frameB);
    setSyncFrame(0);
  }, [frameA, frameB]);

  // Clear alignment
  const clearAlignment = useCallback(() => {
    setOffsetA(0);
    setOffsetB(0);
    setSyncFrame(0);
  }, []);

  // Cycle playback speed
  const cycleSpeed = useCallback(() => {
    const idx = SPEED_ORDER.indexOf(playbackSpeed);
    setPlaybackSpeed(SPEED_ORDER[(idx + 1) % SPEED_ORDER.length]);
  }, [playbackSpeed]);

  const handleSliderChange = useCallback((e) => {
    seekBoth(parseInt(e.target.value, 10));
  }, [seekBoth]);

  // Individual video slider change
  const handleVideoSliderA = useCallback((e) => {
    const f = parseInt(e.target.value, 10);
    setFrameA(f);
    playerARef.current?.seekToFrame(f);
  }, []);

  const handleVideoSliderB = useCallback((e) => {
    const f = parseInt(e.target.value, 10);
    setFrameB(f);
    playerBRef.current?.seekToFrame(f);
  }, []);

  // Callback when player reports frame change
  const handleFrameChangeA = useCallback((frame) => {
    setFrameA(frame);
  }, []);

  const handleFrameChangeB = useCallback((frame) => {
    setFrameB(frame);
  }, []);

  // Render a player (A or B) based on overlay availability
  const renderPlayer = (video, playerRef, label, onFrameChangeCb) => {
    const useCanvas = !video.hasOverlay;

    if (useCanvas) {
      return (
        <CanvasPoseOverlayPlayer
          ref={playerRef}
          key={`canvas-cmp-${video.videoId}`}
          videoId={video.videoId}
          videoUrl={video.videoUrl}
          totalFrames={video.totalFrames}
          fps={label === 'A' ? fpsA : fpsB}
          onFrameChange={onFrameChangeCb}
          apiBaseUrl={API_URL}
          hideControls
        />
      );
    }

    return (
      <SynchronizedPosePlayer
        ref={playerRef}
        key={`sync-cmp-${video.videoId}`}
        videoId={video.videoId}
        analysisData={{
          ...video.analysisData,
          videoUrl: video.videoUrl,
          element_type: video.elementType,
        }}
        onFrameChange={onFrameChangeCb}
        hideControls
      />
    );
  };

  return (
    <div className="vcv-container">
      {/* Header */}
      <div className="vcv-header">
        <h4>Frame-by-Frame Comparison</h4>
        <button className="vcv-exit-btn" onClick={onClose} title="Exit comparison">
          <ArrowLeft size={16} />
          Exit Comparison
        </button>
      </div>

      {/* Dual video grid */}
      <div className="vcv-video-grid">
        <div className={`vcv-video-panel ${isSynced ? 'synced' : ''}`}>
          <div className="vcv-panel-label">
            <div className="vcv-panel-left">
              <span className="vcv-panel-id">A</span>
              <button className="vcv-step-btn" onClick={() => soloStep('A', -1)} title="A: prev frame">
                <ChevronLeft size={14} />
              </button>
              <button className="vcv-step-btn" onClick={() => soloStep('A', 1)} title="A: next frame">
                <ChevronRight size={14} />
              </button>
            </div>
            <span className="vcv-frame-num">Frame {frameA}/{totalA}</span>
          </div>
          <div className="vcv-player-wrap">
            {renderPlayer(videoA, playerARef, 'A', handleFrameChangeA)}
          </div>
          <div className="vcv-video-slider-wrap">
            <input
              type="range"
              min={0}
              max={Math.max(totalA - 1, 0)}
              value={frameA}
              onChange={handleVideoSliderA}
              className="vcv-video-slider"
            />
          </div>
          {coachingA && (
            <div className="vcv-coaching-wrap">
              <FrameAwareCoaching
                frameNumber={frameA}
                coaching={coachingA}
                confidence={coachingA?.confidence_level}
                elementPhase={coachingA?.element_phase}
                showDetailed={true}
              />
            </div>
          )}
        </div>

        <div className={`vcv-video-panel ${isSynced ? 'synced' : ''}`}>
          <div className="vcv-panel-label">
            <div className="vcv-panel-left">
              <span className="vcv-panel-id">B</span>
              <button className="vcv-step-btn" onClick={() => soloStep('B', -1)} title="B: prev frame">
                <ChevronLeft size={14} />
              </button>
              <button className="vcv-step-btn" onClick={() => soloStep('B', 1)} title="B: next frame">
                <ChevronRight size={14} />
              </button>
            </div>
            <span className="vcv-frame-num">Frame {frameB}/{totalB}</span>
          </div>
          <div className="vcv-player-wrap">
            {renderPlayer(videoB, playerBRef, 'B', handleFrameChangeB)}
          </div>
          <div className="vcv-video-slider-wrap">
            <input
              type="range"
              min={0}
              max={Math.max(totalB - 1, 0)}
              value={frameB}
              onChange={handleVideoSliderB}
              className="vcv-video-slider"
            />
          </div>
          {coachingB && (
            <div className="vcv-coaching-wrap">
              <FrameAwareCoaching
                frameNumber={frameB}
                coaching={coachingB}
                confidence={coachingB?.confidence_level}
                elementPhase={coachingB?.element_phase}
                showDetailed={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Timeline slider */}
      <div className="vcv-timeline">
        <input
          type="range"
          min={0}
          max={Math.max(maxSyncFrame, 0)}
          value={syncFrame}
          onChange={handleSliderChange}
          className="vcv-slider"
        />
      </div>

      {/* Minimal control bar */}
      <div className="vcv-controls-minimal">
        <button className="vcv-ctrl-btn" onClick={() => syncStep(-1)} title="Prev frame">
          <ChevronLeft size={18} />
        </button>
        <button className="vcv-play-btn" onClick={syncTogglePlay} title={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
        </button>
        <button className="vcv-ctrl-btn" onClick={() => syncStep(1)} title="Next frame">
          <ChevronRight size={18} />
        </button>

        <div className="vcv-divider" />

        <button
          className={`vcv-sync-toggle ${isSynced ? 'active' : ''}`}
          onClick={isSynced ? clearAlignment : alignFromCurrent}
          title={isSynced ? 'Reset alignment' : 'Sync from current positions'}
        >
          <Link2 size={16} />
        </button>

        <div className="vcv-divider" />

        <button className="vcv-speed-cycle" onClick={cycleSpeed} title="Playback speed">
          {SPEED_LABELS[playbackSpeed]}
        </button>
      </div>
    </div>
  );
};

export default VideoComparisonViewer;
